import { ErrorPrompt, LoadingAnimation, useTheme } from '@karnov/kui-react'
import { useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useConversationQuery } from 'api/conversations/queries'
import { Attachments } from 'components/Attachments'
import { KfcContentWrapper } from 'components/KfcContentWrapper'
import { PrintHeader } from 'components/PrintHeader'
import { ReactMarkdown } from 'components/ReactMarkdown'
import { ReferencesPanel } from 'components/ReferencesPanel'
import { useCountry } from 'hooks/useCountry'

import { useSanitizedContentWithCustomElements } from 'hooks/useSanitizedContentWithCustomElements'

import type { FC } from 'react'

import './print.css'

export const Print: FC = () => {
  const getSanitizedContentWithCustomElements = useSanitizedContentWithCustomElements()

  const isPrinted = useRef(false)

  const country = useCountry()

  const { isDarkMode } = useTheme()

  const [searchParams] = useSearchParams()

  const activeConversationId = searchParams.get('conversationId') ?? ''

  const searchParamMessageId = searchParams.get('messageId')

  const {
    data: messages,
    isError,
    isSuccess,
    isPending,
  } = useConversationQuery(
    { id: activeConversationId },
    {
      select: ({ conversation }) => {
        const aiMessage = conversation.messages.find(message => message.id === searchParamMessageId)

        if (!aiMessage) return undefined

        const userMessage = conversation.messages.find(message => message.id === aiMessage.parentId)

        return { aiMessage, userMessage }
      },
    },
  )

  const handleAfterPrint = useCallback(() => setTimeout(() => {
    if (isDarkMode) document.body.classList.add('dark-theme')
    window.history.back()
  }), [isDarkMode])

  useEffect(() => {
    window.onafterprint = handleAfterPrint
    return () => {
      window.onafterprint = null
    }
  }, [handleAfterPrint])

  useEffect(() => {
    if (
      activeConversationId
      && searchParamMessageId
      && isSuccess
      && !isPrinted.current
    ) {
      isPrinted.current = true
      if (isDarkMode) document.body.classList.remove('dark-theme')
      window.print()
    }
  }, [isSuccess, activeConversationId, searchParamMessageId, isDarkMode])

  const tempCountry = ['dk', 'es', 'fr'].includes(country) ? 'dk' : 'se'

  if (isPending) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <LoadingAnimation height={100} width={100} variant={tempCountry} />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <ErrorPrompt
          errorMessage=""
          shouldReloadOnClick
          locale={country === 'dk' ? 'da' : 'sv'}
        />
      </div>
    )
  }

  const hasAttachments = Boolean(messages?.userMessage?.attachments?.length)

  const userHtmlContent = getSanitizedContentWithCustomElements({
    markdownContent: messages?.userMessage?.content.message,
    messageId: activeConversationId,
    outputFormat: 'print',
  })

  const aiHtmlContent = getSanitizedContentWithCustomElements({
    markdownContent: messages?.aiMessage?.content.message,
    messageId: activeConversationId,
    outputFormat: 'print',
  })

  const kfcContent
    = getSanitizedContentWithCustomElements({
      markdownContent: messages?.aiMessage?.content.kfcMessage || '',
      messageId: activeConversationId,
      outputFormat: 'print',
    }) ?? ''

  return (
    <div className="hidden print:block">
      <PrintHeader />

      {userHtmlContent && <ReactMarkdown content={userHtmlContent} />}

      {hasAttachments && (
        <div className="flex flex-row">
          <Attachments attachments={messages?.userMessage?.attachments} isDetailedView />
        </div>
      )}

      <hr className="my-4 border" />

      {aiHtmlContent && <ReactMarkdown content={aiHtmlContent} />}

      {kfcContent && (
        <KfcContentWrapper>
          <ReactMarkdown content={kfcContent} />
        </KfcContentWrapper>
      )}

      {messages?.aiMessage && (
        <div className="mt-3 flex flex-col gap-y-3 print:block">
          <ReferencesPanel aiMessage={messages.aiMessage} />
        </div>
      )}
    </div>
  )
}
